// Columna central del explorer (`.compact-table` del mock), sobre TanStack Table. SCI y Nivel van en
// COLUMNAS SEPARADAS: el score es el número y el nivel es lo que se deriva de él.
import { useState } from 'react';
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type RowData,
    type SortingState,
    type Table,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { IdText } from '@/shared/components/IdText';
import {
    explainContradiction,
    isContradictoryEvaluation,
    matchesEvaluationFilters,
    NO_VALUE,
    SCORING_PROVIDER_LABEL,
} from '../logic';
import type { ModeliaEvaluationFilters, ModeliaEvaluationRow } from '../types';
import { ContradictionMark, DecisionPill, LevelPill } from './ModeliaPills';

// El ancho de la columna y las clases de su celda viajan en el `meta`, para que header y celda las
// tomen del mismo lugar en el render genérico.
declare module '@tanstack/react-table' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData extends RowData, TValue> {
        widthClass?: string;
        cellClass?: string;
    }
}

/** Filas por página de la tabla; el scroll de la columna no reemplaza a la paginación. */
export const MODELIA_TABLE_PAGE_SIZE = 25;

const CELL = 'border-b border-border/50 px-2 py-[5px] align-middle text-[11px]';
const HEAD =
    'sticky top-0 z-[1] border-b border-border bg-muted px-2 py-[5px] text-left text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

const columns: ColumnDef<ModeliaEvaluationRow>[] = [
    {
        id: 'date',
        header: 'Fecha',
        accessorFn: (row) => row.evaluation.createdAt,
        meta: { widthClass: 'w-[15%]', cellClass: 'text-[10px] tabular-nums' },
        cell: ({ getValue }) => formatDateTimeMx(getValue<string>()),
    },
    {
        id: 'evaluation',
        header: 'Evaluación',
        accessorFn: (row) => row.evaluation.evaluationId,
        meta: { widthClass: 'w-[17%]', cellClass: 'text-[10.5px]' },
        cell: ({ row }) => {
            const contradiction = explainContradiction(row.original.evaluation);
            return (
                <>
                    <IdText value={row.original.evaluation.evaluationId} />
                    {contradiction ? (
                        <div className="mt-0.5">
                            <ContradictionMark title={contradiction} />
                        </div>
                    ) : null}
                </>
            );
        },
    },
    {
        id: 'borrower',
        header: 'Acreditado',
        // Sin nombre resuelto ordena por el id que la fila sí muestra, no por una cadena vacía.
        accessorFn: (row) => row.customerName ?? row.evaluation.borrowerId,
        meta: { widthClass: 'w-[20%]' },
        cell: ({ row }) => (
            <>
                <div className="truncate font-semibold">
                    {row.original.customerName ?? NO_VALUE}
                </div>
                <IdText
                    value={row.original.evaluation.borrowerId}
                    className="block truncate text-[9.5px] text-muted-foreground"
                />
            </>
        ),
    },
    {
        id: 'sci',
        header: 'SCI',
        accessorFn: (row) => row.evaluation.sciTotal,
        meta: { widthClass: 'w-[8%]', cellClass: 'text-right font-bold tabular-nums' },
        cell: ({ getValue }) => getValue<number>(),
    },
    {
        id: 'level',
        header: 'Nivel',
        accessorFn: (row) => row.evaluation.level,
        meta: { widthClass: 'w-[12%]' },
        cell: ({ row }) => <LevelPill level={row.original.evaluation.level} />,
    },
    {
        id: 'decision',
        header: 'Decisión',
        accessorFn: (row) => row.evaluation.decision,
        meta: { widthClass: 'w-[12%]' },
        cell: ({ row }) => <DecisionPill decision={row.original.evaluation.decision} />,
    },
    {
        id: 'provider',
        header: 'Proveedor',
        accessorFn: (row) => SCORING_PROVIDER_LABEL[row.evaluation.provider],
        meta: { widthClass: 'w-[10%]', cellClass: 'truncate text-[10.5px]' },
        cell: ({ getValue }) => getValue<string>(),
    },
    {
        id: 'thinFile',
        header: 'Thin file',
        accessorFn: (row) => (row.evaluation.thinFile ? 'Sí' : 'No'),
        meta: { widthClass: 'w-[6%]', cellClass: 'text-[10.5px] text-muted-foreground' },
        cell: ({ getValue }) => getValue<string>(),
    },
];

/**
 * La tabla sobre las filas YA ACUMULADAS por el cursor del servidor: el filtro, el orden y las
 * páginas trabajan sobre lo que llegó, nunca sobre el listado entero.
 */
export function useModeliaEvaluationsTable(
    rows: ModeliaEvaluationRow[],
    filters: ModeliaEvaluationFilters,
): Table<ModeliaEvaluationRow> {
    const [sorting, setSorting] = useState<SortingState>([{ id: 'date', desc: true }]);
    return useReactTable({
        data: rows,
        columns,
        state: { globalFilter: filters, sorting },
        onSortingChange: setSorting,
        // El panel de filtros ES el filtro global: un solo predicado con todos sus campos.
        globalFilterFn: (row, _columnId, value: ModeliaEvaluationFilters) =>
            matchesEvaluationFilters(row.original, value),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: MODELIA_TABLE_PAGE_SIZE } },
    });
}

export interface ModeliaEvaluationsTableProps {
    table: Table<ModeliaEvaluationRow>;
    selectedEvaluationId: string | null;
    onSelect: (evaluationId: string) => void;
    className?: string;
}

export function ModeliaEvaluationsTable({
    table,
    selectedEvaluationId,
    onSelect,
    className,
}: ModeliaEvaluationsTableProps) {
    const pageRows = table.getRowModel().rows;
    return (
        <div className={cn('min-h-0 flex-1 overflow-y-auto', className)}>
            <table className="w-full table-fixed border-collapse">
                <caption className="sr-only">Evaluaciones SCI del motor de scoring</caption>
                <thead>
                    {table.getHeaderGroups().map((group) => (
                        <tr key={group.id}>
                            {group.headers.map((header) => {
                                const direction = header.column.getIsSorted();
                                return (
                                    <th
                                        key={header.id}
                                        scope="col"
                                        aria-sort={
                                            direction === false
                                                ? 'none'
                                                : direction === 'desc'
                                                  ? 'descending'
                                                  : 'ascending'
                                        }
                                        className={cn(
                                            HEAD,
                                            header.column.columnDef.meta?.widthClass,
                                            header.column.columnDef.meta?.cellClass?.includes(
                                                'text-right',
                                            ) && 'text-right',
                                        )}
                                    >
                                        <button
                                            type="button"
                                            onClick={header.column.getToggleSortingHandler()}
                                            className="inline-flex items-center gap-0.5 uppercase tracking-[0.04em] hover:text-foreground"
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                            {direction === 'asc' ? (
                                                <ChevronUp className="size-2.5" aria-hidden />
                                            ) : null}
                                            {direction === 'desc' ? (
                                                <ChevronDown className="size-2.5" aria-hidden />
                                            ) : null}
                                        </button>
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {pageRows.length === 0 ? (
                        <tr>
                            <td
                                colSpan={columns.length}
                                className="px-2 py-6 text-center text-[11.5px] text-muted-foreground"
                            >
                                Ninguna evaluación coincide con estos filtros.
                            </td>
                        </tr>
                    ) : null}
                    {pageRows.map((row) => {
                        const { evaluation } = row.original;
                        const selected = evaluation.evaluationId === selectedEvaluationId;
                        return (
                            <tr
                                key={evaluation.evaluationId}
                                aria-selected={selected}
                                onClick={() => {
                                    onSelect(evaluation.evaluationId);
                                }}
                                className={cn(
                                    'cursor-pointer transition-colors hover:bg-accent/40',
                                    selected &&
                                        'bg-accent shadow-[inset_3px_0_0_hsl(var(--primary))]',
                                    isContradictoryEvaluation(evaluation) && 'bg-destructive/5',
                                )}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td
                                        key={cell.id}
                                        className={cn(CELL, cell.column.columnDef.meta?.cellClass)}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
